import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Check, KeyRound, ShieldCheck, TriangleAlert } from 'lucide-react';

const STEPS = [
  'Choose how you sign in',
  'Accept the Terms, Privacy Policy and Community Guidelines',
  'Review and sign the Practitioner Agreement',
  'Set up your profile and services',
];

export default function PractitionerInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();

  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('This link is missing its invitation code.');
      return;
    }
    axios
      .get(`${API}/auth/invite/${token}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'This invitation link is not valid.'));
  }, [token]);

  const land = (data) => {
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    toast.success('Your account is yours — a few things to read and sign next');
    window.location.href = '/dashboard/practitioner-onboarding';
  };

  const claimWithPassword = async (e) => {
    e.preventDefault();
    if (password !== confirm) return toast.error("Those two passwords don't match");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/invite/${token}/password`, { password });
      land(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not set your password');
    } finally {
      setBusy(false);
    }
  };

  const claimWithGoogle = () => {
    const redirect = `${window.location.origin}/practitioner-invite/google?token=${token}`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-slate-950">
        <div className="max-w-md w-full text-center rounded-2xl border border-white/10 bg-slate-900/70 backdrop-blur-xl p-10"
          data-testid="invite-error">
          <TriangleAlert className="w-10 h-10 mx-auto mb-5 text-amber-400" />
          <h1 className="text-2xl font-semibold text-white mb-3">We can't open that invitation</h1>
          <p className="text-base text-slate-300 mb-8">{error}</p>
          <Button variant="outline" onClick={() => navigate('/')}
            className="border-white/25 bg-transparent text-white hover:bg-white/10"
            data-testid="invite-error-home-btn">Back to the site</Button>
        </div>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-400">
        Opening your invitation…
      </div>
    );
  }

  const first = (invite.full_name || '').split(' ')[0];

  return (
    <div className="min-h-screen bg-slate-950 px-6 py-16" data-testid="practitioner-invite-page">
      <div className="max-w-4xl mx-auto grid gap-10 md:grid-cols-[1.1fr_1fr]">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-cyan-300/70 mb-4">
            11/11 Collective Connection
          </p>
          <h1 className="text-4xl sm:text-5xl font-semibold text-white leading-[1.1] mb-5">
            Welcome{first ? `, ${first}` : ''}.
          </h1>
          <p className="text-base text-slate-300 leading-relaxed mb-10">
            Your practitioner account has been created for{' '}
            <span className="text-white">{invite.email}</span>. Choose how you'd like to sign in and
            it's yours.
          </p>

          <ol className="space-y-3">
            {STEPS.map((s, i) => (
              <li key={s} className="flex items-start gap-3">
                <span className={`mt-0.5 w-6 h-6 rounded-full grid place-items-center text-xs shrink-0 ${
                  i === 0 ? 'bg-cyan-400/20 text-cyan-200 border border-cyan-300/40'
                          : 'bg-white/5 text-slate-400 border border-white/10'}`}>
                  {i + 1}
                </span>
                <span className={i === 0 ? 'text-white text-sm' : 'text-slate-400 text-sm'}>{s}</span>
              </li>
            ))}
          </ol>
        </div>

        <div className="rounded-2xl border border-white/10 bg-slate-900/70 backdrop-blur-xl p-7">
          <button
            onClick={claimWithGoogle}
            className="w-full flex items-center justify-center gap-3 rounded-xl border border-white/15 bg-white text-slate-900 font-medium py-3 transition-colors duration-200 hover:bg-slate-100"
            data-testid="invite-google-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1a11 11 0 0 0-9.82 6.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-6">
            <span className="h-px flex-1 bg-white/10" />
            <span className="text-xs uppercase tracking-widest text-slate-500">or</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <form onSubmit={claimWithPassword} className="space-y-4">
            <div>
              <Label htmlFor="pw" className="text-slate-300 text-sm">Set a password</Label>
              <Input
                id="pw" type="password" value={password} required
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 bg-slate-950/60 border-white/15 text-white"
                data-testid="invite-password-input"
              />
            </div>
            <div>
              <Label htmlFor="pw2" className="text-slate-300 text-sm">Confirm it</Label>
              <Input
                id="pw2" type="password" value={confirm} required
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-1.5 bg-slate-950/60 border-white/15 text-white"
                data-testid="invite-password-confirm"
              />
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              At least 8 characters, with an uppercase letter, a lowercase letter and a number.
            </p>
            <Button type="submit" disabled={busy} className="w-full" data-testid="invite-submit-btn">
              <KeyRound className="w-4 h-4 mr-2" />
              {busy ? 'Claiming your account…' : 'Claim my account'}
            </Button>
          </form>

          <p className="flex items-start gap-2 text-xs text-slate-500 mt-6 leading-relaxed">
            <ShieldCheck className="w-4 h-4 shrink-0 mt-px text-slate-400" />
            This link works once. Either way you choose, you can add the other sign-in method later
            from your settings.
          </p>
        </div>
      </div>
    </div>
  );
}
